/**
 * Resourcing domain money: decimal in app state; legacy wire (×100) only at HTTP edge.
 * Reuses {@link rateAmountFromBookingWire} / {@link rateAmountToBookingWire} from booking API.
 */

import type {
  Booking,
  Document,
  Expense,
  Production,
  ProductionBudgetExpense,
  ResourceSlot,
  ResourceSlotAssignment
} from '__types__';
import {
  rateAmountFromBookingWire,
  rateAmountToBookingWire
} from 'v5/api/booking/bookingRateAmount.http';
import {
  budgetExpenseLineCreateInputToWire,
  budgetExpenseSlotCreateTemplateToWire,
  resourceSlotCreateTemplateToWire
} from 'v5/api/resourcing/adapters/mutationToWire';
import { isDefined } from 'v5/platform/lang/isDefined';
import { pickBy } from 'lodash';
import type {
  BudgetExpenseCreateInput,
  BudgetExpenseLineCreateInput,
  ResourceSlotCreateInput
} from 'v5/core/production-resourcing/mutation-inputs';
import type {
  BookingExpensesUpdatePatch,
  BookingFeesPatch,
  BookingMutationWirePatch,
  BookingRateAndFeesPatch,
  BookingRatePatch,
  BookingStatusPatch,
  BudgetExpensePlanPatch,
  BudgetExpenseUpdatePatch,
  ExpenseLineUpdatePatch,
  ResourceSlotPlanPatch,
  ResourceSlotUpdatePatch
} from './resourcing.mutation.types';

export { rateAmountFromBookingWire, rateAmountToBookingWire };
export {
  bookingRateIntervalToWire,
  expenseRateIntervalToWire,
  parseBookingRateInterval,
  parseExpenseRateInterval
} from './resourcingInterval.http';

const RATE_AMOUNT_KEYS = [
  'rate_amount',
  'estimate_rate_amount',
  'commit_rate_amount'
] as const;

type RateAmountFieldKey = (typeof RATE_AMOUNT_KEYS)[number];

function decimalAmountToWire(
  decimal: number | null | undefined
): number | null | undefined {
  if (decimal == null) return decimal;
  return rateAmountToBookingWire(decimal);
}

function readRateAmountValue(
  patch: object,
  key: RateAmountFieldKey
): number | null | undefined {
  if (!(key in patch)) {
    return undefined;
  }
  const value = Reflect.get(patch, key);
  if (typeof value === 'number' || value === null) {
    return value;
  }
  return undefined;
}

function mapRateAmountFieldInPatch<T extends object>(
  patch: T,
  convert: (n: number | null | undefined) => number | null | undefined
): T {
  return RATE_AMOUNT_KEYS.reduce<T>(
    (acc, key) => {
      const value = readRateAmountValue(acc, key);
      if (value === undefined) {
        return acc;
      }
      const converted = convert(value);
      if (converted === value) {
        return acc;
      }
      return { ...acc, [key]: converted };
    },
    { ...patch }
  );
}

type WithRateAmount = { rate_amount?: number | null };

type WithCommitRateAmount = { commit_rate_amount?: number | null };

type WithEstimateRateAmount = { estimate_rate_amount?: number | null };

/** Normalize a loose Redux booking record for resourcing commits / diffs. */
export function normalizeBookingRecord<T extends WithRateAmount>(
  booking: T | undefined
): T | undefined {
  if (booking == null) return undefined;
  if (booking.rate_amount == null || typeof booking.rate_amount !== 'number') {
    return booking;
  }
  const decimal = rateAmountFromBookingWire(booking.rate_amount);
  if (decimal === booking.rate_amount) return booking;
  return { ...booking, rate_amount: decimal };
}

export function normalizeExpenseRecord<T extends WithCommitRateAmount>(
  expense: T
): T {
  if (
    expense.commit_rate_amount == null ||
    typeof expense.commit_rate_amount !== 'number'
  ) {
    return expense;
  }
  const decimal = rateAmountFromBookingWire(expense.commit_rate_amount);
  if (decimal === expense.commit_rate_amount) return expense;
  return { ...expense, commit_rate_amount: decimal };
}

export function normalizeResourceSlotEstimateRecord<
  T extends WithEstimateRateAmount
>(slot: T): T {
  if (
    slot.estimate_rate_amount == null ||
    typeof slot.estimate_rate_amount !== 'number'
  ) {
    return slot;
  }
  const decimal = rateAmountFromBookingWire(slot.estimate_rate_amount);
  if (decimal === slot.estimate_rate_amount) return slot;
  return { ...slot, estimate_rate_amount: decimal };
}

export function normalizeBookingForResourcing<T extends Booking>(
  booking: T
): T {
  let next: T = booking;
  if (booking.rate_amount != null) {
    const decimal = rateAmountFromBookingWire(booking.rate_amount);
    if (decimal !== booking.rate_amount) {
      next = { ...next, rate_amount: decimal ?? undefined };
    }
  }
  if (booking.invoices?.length) {
    const normalizedInvoices = booking.invoices.map(normalizeInvoiceDocument);
    if (
      normalizedInvoices.some((inv, index) => inv !== booking.invoices[index])
    ) {
      next = { ...next, invoices: normalizedInvoices };
    }
  }
  return next;
}

export function normalizeExpenseForResourcing<T extends Expense>(
  expense: T
): T {
  if (expense.commit_rate_amount == null) return expense;
  const decimal = rateAmountFromBookingWire(expense.commit_rate_amount);
  if (decimal === expense.commit_rate_amount) return expense;
  return { ...expense, commit_rate_amount: decimal ?? undefined };
}

function normalizeInvoiceDocument(document: Document): Document {
  const amount = document.document_data?.amount;
  if (amount == null || !Number.isFinite(amount)) {
    return document;
  }
  const decimal = rateAmountFromBookingWire(amount);
  if (decimal === amount) {
    return document;
  }
  return {
    ...document,
    document_data: { ...document.document_data, amount: decimal ?? undefined }
  };
}

function normalizeReceipts<T extends Expense>(entity: T): T {
  if (!entity.receipts?.length) {
    return entity;
  }
  const receipts = entity.receipts.map(normalizeInvoiceDocument);
  if (receipts.every((receipt, index) => receipt === entity.receipts[index])) {
    return entity;
  }
  return { ...entity, receipts };
}

/** Production slice normalized for resourcing billing (see {@link ProductionForBilling}). */
export type ProductionResourcingNormalizeInput = Partial<Production> & {
  resource_slots?: Array<
    Pick<ResourceSlot, 'estimate_rate_amount'> &
      Partial<ResourceSlot> & {
        archived?: boolean;
        resource_slot_assignments?: ResourceSlotAssignment[];
      }
  >;
  budget_expenses?: Array<
    Pick<ProductionBudgetExpense, 'estimate_rate_amount'> &
      Partial<ProductionBudgetExpense> & {
        archived?: boolean;
        expenses?: Expense[];
      }
  >;
};

export function normalizeProductionForResourcing<
  T extends ProductionResourcingNormalizeInput
>(production: T): T {
  const resource_slots = production.resource_slots?.map(slot => {
    if (slot.estimate_rate_amount == null) return slot;
    const decimal = rateAmountFromBookingWire(slot.estimate_rate_amount);
    if (decimal === slot.estimate_rate_amount) return slot;
    return { ...slot, estimate_rate_amount: decimal };
  });

  const budget_expenses = production.budget_expenses?.map(be => {
    let next = be;
    if (be.estimate_rate_amount != null) {
      const decimal = rateAmountFromBookingWire(be.estimate_rate_amount);
      if (decimal !== be.estimate_rate_amount) {
        next = { ...next, estimate_rate_amount: decimal ?? undefined };
      }
    }
    if (be.expenses?.length) {
      next = {
        ...next,
        expenses: be.expenses.map(e =>
          normalizeReceipts(normalizeExpenseForResourcing(e))
        )
      };
    }
    return next;
  });

  if (
    resource_slots === production.resource_slots &&
    budget_expenses === production.budget_expenses
  ) {
    return production;
  }
  return {
    ...production,
    ...pickBy({ resource_slots, budget_expenses }, isDefined)
  };
}

type BookingRateMutationWirePatch =
  | BookingRatePatch
  | BookingFeesPatch
  | BookingRateAndFeesPatch
  | BookingStatusPatch;

export function toWireBookingPatch(
  patch: BookingRateMutationWirePatch
): BookingRateMutationWirePatch {
  return mapRateAmountFieldInPatch(patch, decimalAmountToWire);
}

/** Legacy v1 booking PUT: decimal `rate_amount` → wire when present. */
export function toWireLegacyBookingPatch<T extends WithRateAmount>(
  patch: T
): T {
  return mapRateAmountFieldInPatch(patch, decimalAmountToWire);
}

export function toWireResourceSlotPatch(
  patch: ResourceSlotUpdatePatch | ResourceSlotPlanPatch
): typeof patch {
  return mapRateAmountFieldInPatch(patch, decimalAmountToWire);
}

export function toWireBudgetExpensePatch(
  patch: BudgetExpenseUpdatePatch | BudgetExpensePlanPatch
): typeof patch {
  return mapRateAmountFieldInPatch(patch, decimalAmountToWire);
}

export function toWireExpenseLinePatch(
  patch: ExpenseLineUpdatePatch
): ExpenseLineUpdatePatch {
  return mapRateAmountFieldInPatch(patch, decimalAmountToWire);
}

export function toWireBookingExpensesPatch(
  patch: BookingExpensesUpdatePatch
): BookingExpensesUpdatePatch {
  return {
    expenses: patch.expenses.map(exp =>
      mapRateAmountFieldInPatch(exp, decimalAmountToWire)
    )
  };
}

export function toWireResourceSlotCreateInput(
  input: ResourceSlotCreateInput
): Record<string, unknown> {
  const { group_id, order, ...template } = input;
  return mapRateAmountFieldInPatch(
    {
      group_id,
      order,
      ...resourceSlotCreateTemplateToWire(template)
    },
    decimalAmountToWire
  );
}

export function toWireBudgetExpenseCreateInput(
  input: BudgetExpenseCreateInput
): BudgetExpenseCreateInput {
  const { group_id, order, ...template } = input;
  return mapRateAmountFieldInPatch(
    {
      group_id,
      order,
      ...budgetExpenseSlotCreateTemplateToWire(template)
    },
    decimalAmountToWire
  ) as BudgetExpenseCreateInput;
}

export function toWireBudgetExpenseLineCreateInput(
  input: BudgetExpenseLineCreateInput
): Record<string, unknown> {
  return mapRateAmountFieldInPatch(
    budgetExpenseLineCreateInputToWire(input),
    decimalAmountToWire
  );
}

function isBookingExpensesWirePatch(
  patch: BookingMutationWirePatch
): patch is BookingExpensesUpdatePatch {
  if (!('expenses' in patch) || !Array.isArray(patch.expenses)) {
    return false;
  }
  if (patch.expenses.length === 0) {
    return true;
  }
  const first = patch.expenses[0];
  return first != null && 'commit_rate_amount' in first;
}

/** HTTP edge for resourcing booking mutations (`mutationToWire` output). */
export function toWireBookingMutationPatch(
  patch: BookingMutationWirePatch
): BookingMutationWirePatch {
  if (isBookingExpensesWirePatch(patch)) {
    return toWireBookingExpensesPatch(patch);
  }
  return toWireBookingPatch(patch);
}
